import { FuturesAsSpotConfirmation } from "features/bots/BotForm/Advanced/FollowFuturesAsSpot";
import { getBotMethod, useOnboardingAnalytics } from "analytics/onboarding";
import { useContext } from "react";
import { useSelector } from "react-redux";
import { selectWorkspace } from "@shared/slices/common.slice";
import { BotType, WorkspaceType } from "@shared/types";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import {
  BOT_FORM_FINISH_SETUP_ID,
  BOT_FORM_TRADINGVIEW_DETAILS_ID,
} from "testIds";
import Steps from "common/Steps/Steps";
import { ConfirmBotModalStep } from "@shared/features/bots/BotForm/common/types";
import Flex from "common/Boxes/Flex";
import TradingViewUUIDs from "features/bots/MyBots/TradingViewUUIDs";
import Modal from "common/Modal/Modal";
import React, { ChangeEvent } from "react";
import Text from "common/Typography/Text";
import CopyOutlined from "@ant-design/icons/CopyOutlined";
import WarningOutlined from "@ant-design/icons/WarningOutlined";
import { typography } from "@shared/common/Themes/typography";
import { CopyToClipboardButton } from "common/CopyToClipboard/CopyToClipboard";
import styled, { useTheme } from "styled-components";
import Title from "common/Typography/Title";
import { BotData } from "@shared/types";
import useConfirmBotModal from "@shared/features/bots/BotForm/common/hooks/useConfirmBotModal";
import { ErrorText, FormFieldTitle } from "features/bots/common/styles";
import Input from "common/Inputs/Input";
import AnimatedCheckIcon from "common/Animations/AnimatedCheckIcon";
import StartGroupFreeTrialModal from "features/bots/BotForm/common/StartGroupFreeTrialModal";
import { getTvWebhookBaseUrl } from "app/shared/utils";
import StyledRadioGroup from "common/Radio/StyledRadioGroup";
import Button from "common/Buttons/Button";
import DebouncedButton from "common/Buttons/DebouncedButton";
import { BOT_FORM_CONFIRM_SAVE_BUTTON_ID } from "testIds.ts";

interface ConfirmBotModalProps {
  actionLabel?: string;
  closeModal: () => void;
  saveChanges: (activate?: boolean) => Promise<void> | undefined;
  isOpen: boolean;
  botData?: BotData;
  isLoading?: boolean;
  onSuccess?: (botData?: BotData) => void;
  gridBotValuesChanged?: boolean;
}

const ConfirmBotModal = ({
  actionLabel,
  closeModal,
  saveChanges,
  isOpen,
  botData,
  isLoading,
  onSuccess,
  gridBotValuesChanged,
}: ConfirmBotModalProps) => {
  const appTheme = useTheme();
  const onboarding = useContext(OnboardingContext);
  const form = useContext(BotFormContext);
  const isOnboardingTradingView =
    !!onboarding &&
    (form?.botType === BotType.TRADING_VIEW ||
      form?.botType === BotType.TRADING_VIEW_SIGNALS);
  const workspace = useSelector(selectWorkspace);
  const demoOnboarding = !!onboarding && workspace === WorkspaceType.DEMO;
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const {
    steps,
    successMessage,
    title,
    okText,
    isOkButtonDisabled,
    cancelText,
    onOk,
    onCancel: closeConfirm,
    step,
    parsedStep,
    showSteps,
    contentData,
    radioGroupData,
    footer,
    nameInputData,
    freeTrialModalData,
  } = useConfirmBotModal({
    saveChanges,
    onSuccess: (createdBot) => onSuccess?.(createdBot || botData),
    closeModal,
    isOpen,
    gridBotValuesChanged,
  });

  const trackOnboarding = useOnboardingAnalytics();
  // Onboarding: leaving the confirmation without creating is a drop-off.
  const onCancel = () => {
    if (onboarding && step !== ConfirmBotModalStep.GET_UUIDS)
      trackOnboarding("Bot Confirm Cancelled", {
        bot_type: getBotMethod(form?.botType),
      });
    closeConfirm();
  };

  const getConfirmOptions = () => {
    if (!radioGroupData.options) return undefined;
    return (
      <StyledRadioGroup
        value={radioGroupData.value}
        options={radioGroupData.options}
        onChange={radioGroupData.setValue}
        style={{ marginTop: contentData ? 12 : 0 }}
        textStyle={{ fontWeight: "500" }}
      />
    );
  };

  const getNameInput = () => {
    if (!nameInputData.shouldDisplay) return;
    return (
      <NameInputWrapper>
        <FormFieldTitle>{nameInputData.title}</FormFieldTitle>
        <Input
          value={nameInputData.value}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            nameInputData.onChange(e.target.value)
          }
        />
        {nameInputData.error && <ErrorText>{nameInputData.error}</ErrorText>}
      </NameInputWrapper>
    );
  };

  const buttons = step === ConfirmBotModalStep.GET_UUIDS && (
    <div style={{ marginTop: 32, textAlign: "center" }}>
      <CopyToClipboardButton
        name="Webhook URL"
        text={getTvWebhookBaseUrl()}
        icon={<CopyOutlined />}
        type="primary"
        shape={undefined}
        size="large"
        style={{ width: "100%" }}
      >
        Webhook URL
      </CopyToClipboardButton>
      <Button
        data-testid={BOT_FORM_FINISH_SETUP_ID}
        type="primary"
        size="large"
        onClick={onCancel}
        style={{ marginTop: 16, width: "100%" }}
      >
        {t("revision.finish_setup")}
      </Button>
    </div>
  );

  const getCreateStepContent = () => {
    if (!contentData) return null;
    return (
      <>
        {contentData.areYouSure && (
          <Text style={{ fontWeight: "500", marginBottom: 6 }}>
            {contentData.areYouSure}
          </Text>
        )}
        {(isOnboardingTradingView
          ? [t("revision.tradingview_confirmation")]
          : contentData.generalContent
        )?.map((text, index) => (
          <Text as="div" style={{ marginTop: index === 0 ? 0 : 6 }} key={index}>
            {text}
          </Text>
        ))}
        {contentData.leverageWarning && (
          <Flex
            $alignItems="flex-start"
            style={{ marginTop: 8, textAlign: "start" }}
          >
            <WarningOutlined
              style={{
                fontSize: typography.fontSizes.L,
                marginRight: 8,
                color: appTheme.colors.primaryGrey,
              }}
            />
            <Text $size={typography.fontSizes.S}>
              {contentData.leverageWarning}
            </Text>
          </Flex>
        )}
      </>
    );
  };

  const getTradingViewUuidsContent = () => {
    if (step !== ConfirmBotModalStep.GET_UUIDS || !botData) return;
    return (
      <Flex
        data-testid={BOT_FORM_TRADINGVIEW_DETAILS_ID}
        $flexDirection="column"
        $justifyContent="center"
      >
        <AnimatedCheckIcon size={60} color={appTheme.colors.lightGreen} />
        <BoldTitle style={{ margin: "12px 0" }}>{successMessage}</BoldTitle>
        <TradingViewUUIDs
          botData={botData}
          style={{ width: "100%", borderTop: "none" }}
        />
      </Flex>
    );
  };

  const footerComponent =
    footer === false ? (
      footer
    ) : (
      <Flex $justifyContent="flex-end" style={{ columnGap: 8 }}>
        <Button onClick={onCancel}>{cancelText ?? "Cancel"}</Button>
        <DebouncedButton
          data-testid={BOT_FORM_CONFIRM_SAVE_BUTTON_ID}
          type="primary"
          loading={isLoading}
          disabled={isLoading || isOkButtonDisabled}
          onClick={onOk}
        >
          {actionLabel ??
            (demoOnboarding ? t("revision.create_demo_bot") : okText ?? "OK")}
        </DebouncedButton>
      </Flex>
    );

  const items = steps.map((step) => ({ title: step }));

  return (
    <>
      <Modal
        title={title}
        open={isOpen}
        footer={footerComponent}
        onCancel={onCancel}
      >
        {showSteps && (
          <Steps
            current={parsedStep}
            items={items}
            style={{ margin: "0 0 24px 0" }}
          />
        )}
        {demoOnboarding && <Text>{t("guided.virtual_only")}</Text>}
        {getCreateStepContent()}
        {getConfirmOptions()}
        {contentData &&
          form?.botType === BotType.SIGNAL &&
          !form.isChannelConfiguration &&
          form.followFuturesAsSpot === true && <FuturesAsSpotConfirmation />}
        {getNameInput()}
        {getTradingViewUuidsContent()}
        {buttons}
      </Modal>
      <StartGroupFreeTrialModal
        isOpen={freeTrialModalData.isOpen}
        closeModal={freeTrialModalData.onCancel}
        saveChanges={freeTrialModalData.saveChanges}
      />
    </>
  );
};

export default ConfirmBotModal;

const BoldTitle = styled(Title).attrs({ level: 2 })`
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;
const NameInputWrapper = styled.div`
  margin-top: 16px;
  padding: 0 16px;
`;
